import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faExternalLinkAlt,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const formatDate = (value) => {
  if (!value) return '—';
  const [date, time] = value.split(' ');
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}${time ? ' ' + time.slice(0, 5) : ''}`;
};

/**
 * Details of a "Nouvelles soumissions F2F" notification: the submissions synced since the
 * previous one, per survey (per vague for trackers) and per supervisor.
 */
const F2fNewSubmissions = () => {
  const { digestId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('');
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/control/f2f-new-submissions/${digestId}`);
        if (!cancelled && response.data.success) {
          setData(response.data.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.status === 404
            ? 'Cette notification est introuvable.'
            : 'Erreur lors du chargement des soumissions.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [digestId]);

  const groupKey = (g) => `${g.survey_id}-${g.wave_id || 0}`;

  const surveys = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    data.groups.forEach((g) => seen.set(g.survey_id, g.survey_title));
    return Array.from(seen, ([id, title]) => ({ id, title }));
  }, [data]);

  // Filter rows, then drop the supervisors / groups left empty.
  const groups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.groups
      .filter((g) => !surveyFilter || String(g.survey_id) === surveyFilter)
      .map((g) => {
        const supervisors = g.supervisors
          .map((s) => {
            const submissions = !q ? s.submissions : s.submissions.filter((row) =>
              [row.response_id, row.ref, row.interviewer?.name, row.interviewer?.ref, s.supervisor_name, s.supervisor_ref]
                .some((v) => v && String(v).toLowerCase().includes(q))
            );
            return { ...s, submissions };
          })
          .filter((s) => s.submissions.length > 0);
        return { ...g, supervisors, shown: supervisors.reduce((n, s) => n + s.submissions.length, 0) };
      })
      .filter((g) => g.shown > 0);
  }, [data, search, surveyFilter]);

  const shownCount = groups.reduce((n, g) => n + g.shown, 0);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error || 'Aucune donnée.'}</div>
        <button onClick={() => navigate('/notifications')} className="mt-4 text-red-600 hover:underline">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />Retour aux notifications
        </button>
      </div>
    );
  }

  const { digest } = data;

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Nouvelles soumissions F2F</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            {digest.submissions_count} soumission{digest.submissions_count > 1 ? 's' : ''} notifiée{digest.submissions_count > 1 ? 's' : ''} le {formatDate(digest.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/control/f2f-new-submissions/${digest.previous_id}`)}
            disabled={!digest.previous_id}
            className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="mr-1" />Précédente
          </button>
          <button
            onClick={() => navigate(`/control/f2f-new-submissions/${digest.next_id}`)}
            disabled={!digest.next_id}
            className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivante<FontAwesomeIcon icon={faChevronRight} className="ml-1" />
          </button>
        </div>
      </div>

      {/* Summary per survey / vague */}
      <div className="bg-white rounded-lg shadow mb-6 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-red-600 text-white">
            <tr>
              <th className="px-4 py-2 text-left">Enquête</th>
              <th className="px-4 py-2 text-left">Vague</th>
              <th className="px-4 py-2 text-right">Soumissions</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((g) => (
              <tr key={groupKey(g)} className="border-b border-gray-100">
                <td className="px-4 py-2">{g.survey_title}</td>
                <td className="px-4 py-2">{g.is_tracker ? g.wave_label : '—'}</td>
                <td className="px-4 py-2 text-right font-semibold">{g.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4 flex flex-col sm:flex-row gap-4 sm:items-end">
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ID LimeSurvey, ref, enquêteur, prestataire..."
              className="w-full h-10 pl-9 pr-3 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
        {surveys.length > 1 && (
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Enquête</label>
            <select
              value={surveyFilter}
              onChange={(e) => setSurveyFilter(e.target.value)}
              className="w-full sm:w-auto h-10 px-3 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 min-w-[200px]"
            >
              <option value="">Toutes les enquêtes</option>
              {surveys.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
        )}
        <div className="text-sm text-gray-600 sm:pb-2">{shownCount} affichée{shownCount > 1 ? 's' : ''}</div>
      </div>

      {groups.length === 0 && (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Aucune soumission ne correspond aux filtres.</div>
      )}

      {groups.map((g) => {
        const key = groupKey(g);
        const isCollapsed = collapsed[key];
        return (
          <div key={key} className="bg-white rounded-lg shadow mb-6">
            <button
              onClick={() => setCollapsed({ ...collapsed, [key]: !isCollapsed })}
              className="w-full flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 text-left"
            >
              <div>
                <h2 className="text-lg font-bold text-gray-800">{g.survey_title}</h2>
                {g.is_tracker && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800">
                    Tracker — {g.wave_label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <span className="font-semibold">{g.shown} soumission{g.shown > 1 ? 's' : ''}</span>
                <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} />
              </div>
            </button>

            {!isCollapsed && g.supervisors.map((s) => (
              <div key={s.supervisor_id || 'none'} className="px-4 sm:px-6 py-4">
                <h3 className="font-semibold text-red-800 mb-2">
                  {s.supervisor_name
                    ? <>Prestataire : {s.supervisor_name}{s.supervisor_ref && <span className="text-gray-500 font-normal"> ({s.supervisor_ref})</span>}</>
                    : 'Sans prestataire'}
                  <span className="text-gray-500 font-normal"> — {s.submissions.length}</span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left">ID LimeSurvey</th>
                        <th className="px-3 py-2 text-left">Réalisée par</th>
                        <th className="px-3 py-2 text-left">Ref</th>
                        <th className="px-3 py-2 text-left">Date de soumission</th>
                        <th className="px-3 py-2 text-left">Reçue le</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.submissions.map((row) => (
                        <tr key={row.response_id} className="border-b border-gray-100">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {row.limesurvey_url ? (
                              <a href={row.limesurvey_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                                {row.response_id}<FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1 text-xs" />
                              </a>
                            ) : row.response_id}
                          </td>
                          <td className="px-3 py-2">
                            {row.done_by === 'supervisor'
                              ? <span className="italic text-gray-700">Prestataire lui-même</span>
                              : (row.interviewer?.name || 'Enquêteur inconnu')}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.ref || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.submitted_at)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.received_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default F2fNewSubmissions;
